import { AuthStorageInstance } from 'services/Storage/AuthStorage'
import { clearUserPrefs } from 'utils/userPrefs'
import { Mixpanel } from 'services/Tracking/Mixpanel'
import { LinkManager } from '../../utils'

let isRedirecting = false

export type AuthRedirectReason = 'sessionExpired' | 'logout'

const OVERLAY_COPY: Record<AuthRedirectReason, { title: string; subtitle: string }> = {
  sessionExpired: { title: 'Session Expired', subtitle: 'Redirecting to login...' },
  logout: { title: 'Logging Out', subtitle: 'Redirecting to login...' }
}

/**
 * Centralized auth-exit handler. Ensures only one redirect fires even when
 * multiple parallel API calls fail with 401 simultaneously.
 * Shows an overlay before redirecting to login; the copy depends on why we
 * are leaving — a real 401 ("Session Expired") vs a deliberate logout.
 */
export const handleUnauthorizedRedirect = (reason: AuthRedirectReason = 'sessionExpired') => {
  if (isRedirecting) {
    return
  }
  isRedirecting = true

  // Clear subscription storage directly to avoid circular dependency
  // (useSubscription → SubscriptionApi → ApiService → authRedirectHandler)
  if (typeof window !== 'undefined') {
    localStorage.removeItem('okc_subscription_expiry_time')
  }
  // Per-user local choices (premium icon opt-out, …) never outlive the session
  clearUserPrefs()
  AuthStorageInstance.clear()
  Mixpanel.reset()

  if (typeof document !== 'undefined') {
    const overlay = document.createElement('div')
    overlay.id = 'session-expired-overlay'
    overlay.style.cssText = [
      'position:fixed',
      'top:0',
      'left:0',
      'width:100vw',
      'height:100vh',
      'display:flex',
      'align-items:center',
      'justify-content:center',
      'background:rgba(255,255,255,0.95)',
      'z-index:99999',
      'font-family:Noto Sans,sans-serif'
    ].join(';')
    const { title, subtitle } = OVERLAY_COPY[reason]
    overlay.innerHTML = `
      <div style="text-align:center">
        <h2 style="margin:0 0 8px;color:#333;font-size:20px">${title}</h2>
        <p style="margin:0;color:#666;font-size:14px">${subtitle}</p>
      </div>
    `
    document.body.appendChild(overlay)
  }

  setTimeout(() => {
    window.location.replace(LinkManager.getLoginLink())
  }, 800)
}

export const isUnauthorizedRedirectInProgress = () => isRedirecting

/**
 * Reset the redirect flag. Only used for testing or after
 * a successful login restores auth state.
 */
export const resetUnauthorizedRedirectFlag = () => {
  isRedirecting = false
}
